import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import type { JSX } from 'react';
import React from 'react';

import { Icon } from '@feathr/components';
import SegmentSelect from '@feathr/extender/components/SegmentSelect';
import type { ISegmentSelectSimpleChangeProps } from '@feathr/extender/components/SegmentSelectSimple';

import * as styles from './DraggableSegmentSelect.css';

export type TSegmentContainerId = 'included' | 'excluded';

interface IDraggableSegmentSelectProps {
  affectedCampaigns: string | undefined;
  affectedGoals: number | undefined;
  containerId: TSegmentContainerId;
  disabled: boolean;
  excludeIds: string[];
  id: string;
  isBeingDragged: boolean;
  onChange: (props: ISegmentSelectSimpleChangeProps) => void;
  onRemove: (id?: string) => void;
  value: string;
}

function DraggableSegmentSelect({
  affectedCampaigns,
  affectedGoals,
  containerId,
  disabled,
  excludeIds,
  id,
  isBeingDragged,
  onChange,
  onRemove,
  value,
}: Readonly<IDraggableSegmentSelectProps>): JSX.Element {
  const isPlaceholder = !id;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: id || 'placeholder',
    data: { containerId },
    disabled: disabled || isPlaceholder,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={classNames(styles.root, { [styles.dragging]: isBeingDragged })}
      ref={setNodeRef}
      style={style}
    >
      <div
        className={classNames(styles.handle, {
          [styles.handleDisabled]: disabled || isPlaceholder,
        })}
        {...(isPlaceholder ? {} : { ...attributes, ...listeners })}
      >
        <Icon icon={faGripDotsVertical} />
      </div>
      <div className={styles.content}>
        <SegmentSelect
          affectedCampaigns={affectedCampaigns}
          affectedGoals={affectedGoals}
          disabled={disabled}
          excludeIds={excludeIds}
          hideIncluded={true}
          name={'segment_select'}
          onChange={onChange}
          onRemove={onRemove}
          stat={'num_emails_total'}
          value={value}
        />
      </div>
    </div>
  );
}

export default DraggableSegmentSelect;
